import React from 'react';
import { CheckCircle2, type LucideIcon } from 'lucide-react';

type PreflightTone = 'ok' | 'info' | 'warn' | 'error';

const toneClasses: Record<PreflightTone, { container: string; iconWrap: string; badge: string }> = {
  ok: {
    container: 'border-emerald-500/30 bg-emerald-500/5',
    iconWrap: 'text-emerald-600',
    badge: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  },
  // Neutral "the run will do something you should know about" — tenant primary tint, so it reads as
  // information rather than a problem next to the amber warnings.
  info: {
    container: 'border-primary/25 bg-primary/5',
    iconWrap: 'text-primary',
    badge: 'bg-primary/10 text-primary border-primary/30',
  },
  warn: {
    container: 'border-amber-500/30 bg-amber-500/5',
    iconWrap: 'text-amber-600',
    badge: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  },
  error: {
    container: 'border-destructive/30 bg-destructive/5',
    iconWrap: 'text-destructive',
    badge: 'bg-destructive/10 text-destructive border-destructive/30',
  },
};

interface PreflightSectionProps {
  icon: LucideIcon;
  tone: PreflightTone;
  title: string;
  count: number;
  names: string[];
  /** The list stops at ten names unless a warning has to account for everyone. */
  listEveryName?: boolean;
  footnote?: string;
  testId?: string;
  children?: React.ReactNode;
}

export const PreflightSection: React.FC<PreflightSectionProps> = ({ icon: Icon, tone, title, count, names, listEveryName, footnote, testId, children }) => {
  const isOk = tone === 'ok' || count === 0;
  const DisplayIcon = isOk ? CheckCircle2 : Icon;
  const displayTone = isOk ? 'ok' : tone;
  const displayClasses = toneClasses[displayTone];
  return (
    <div className={`rounded-lg border p-3 ${displayClasses.container}`} data-testid={testId}>
      <div className="flex items-start gap-3">
        <DisplayIcon className={`mt-0.5 ${displayClasses.iconWrap}`} size={20} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <span className="font-semibold text-sm text-foreground">{title}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${displayClasses.badge}`}>
              {isOk ? '✓' : count}
            </span>
          </div>
          {!isOk && names.length > 0 && (
            <ul className="mt-2 text-xs text-foreground/80 list-disc ps-4 space-y-0.5 max-h-28 overflow-y-auto tabular-nums">
              {(listEveryName ? names : names.slice(0, 10)).map((n, i) => <li key={i}>{n}</li>)}
              {!listEveryName && names.length > 10 && <li className="italic text-muted-foreground">…+{names.length - 10}</li>}
            </ul>
          )}
          {footnote && <p className="mt-2 text-xs text-muted-foreground">{footnote}</p>}
          {children}
        </div>
      </div>
    </div>
  );
};
